import React, { useState, useEffect } from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Cookie, X, Shield, BarChart3, Megaphone } from 'lucide-react';

const CONSENT_KEY = 'cookie-consent';

export type CookiePreferences = {
  necessary: boolean; // always true
  analytics: boolean;
  marketing: boolean;
};

const DEFAULT_PREFS: CookiePreferences = { necessary: true, analytics: false, marketing: false };

/** Read stored preferences */
export function getCookiePreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Migration: add marketing if missing from old format
    if (typeof parsed.marketing === 'undefined') {
      parsed.marketing = false;
    }
    return parsed as CookiePreferences;
  } catch {
    return null;
  }
}

/** Save preferences and apply them */
function savePreferences(prefs: CookiePreferences) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
  applyPreferences(prefs);
}

/** Apply consent to GA4 + marketing + analytics tools */
function applyPreferences(prefs: CookiePreferences) {
  // GA4 consent mode
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag('consent', 'update', {
      'analytics_storage': prefs.analytics ? 'granted' : 'denied',
      'ad_storage': prefs.marketing ? 'granted' : 'denied',
      'ad_user_data': prefs.marketing ? 'granted' : 'denied',
      'ad_personalization': prefs.marketing ? 'granted' : 'denied',
    });
  }
  // Marketing tools (e.g. traffic identification) – only init when marketing accepted
  if (prefs.marketing && typeof (window as any).initSnitcher === 'function') {
    (window as any).initSnitcher();
  }
  // Admin signals — staff sessions are NEVER recorded, regardless of consent
  const isAdminPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  const isAdminFlagged = typeof localStorage !== 'undefined' && localStorage.getItem('funnel_is_admin') === 'true';
  const isAdmin = isAdminPath || isAdminFlagged;

  const sessionId = sessionStorage.getItem('funnel_session_id') || '';
  const visitorId = localStorage.getItem('funnel_visitor_id') || '';

  // PostHog — TEMPORARILY treated as essential diagnostics so we can verify
  // whether missing recordings are due to declined consent. Always initialized
  // for non-admin visitors with a session, regardless of `prefs.analytics`.
  if (sessionId && visitorId && !isAdmin) {
    import('@/lib/posthogInit').then(m => m.initPostHog(sessionId, visitorId)).catch(() => {});
  } else if (isAdmin) {
    import('@/lib/posthogInit').then(m => m.stopPostHog()).catch(() => {});
  }

  // OpenReplay — remains strictly consent-gated
  if (prefs.analytics) {
    if (sessionId && visitorId && !isAdmin) {
      import('@/lib/openreplayInit').then(m => m.initOpenReplay(sessionId, visitorId)).catch(() => {});
    } else if (isAdmin) {
      import('@/lib/openreplayInit').then(m => m.stopOpenReplay()).catch(() => {});
    }
  } else {
    import('@/lib/openreplayInit').then(m => m.stopOpenReplay()).catch(() => {});
  }
}

/** Allow external code (Cookie Policy page) to re-open the banner */
let openBannerFn: (() => void) | null = null;
export function openCookieSettings() {
  openBannerFn?.();
}

export const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    const prefs = getCookiePreferences();
    if (prefs) {
      applyPreferences(prefs);
    } else {
      // Even before consent: trigger PostHog as essential diagnostics (admin-guarded inside)
      applyPreferences(DEFAULT_PREFS);
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Register the open function
  useEffect(() => {
    openBannerFn = () => {
      const prefs = getCookiePreferences();
      setAnalyticsEnabled(prefs?.analytics ?? false);
      setMarketingEnabled(prefs?.marketing ?? false);
      setShowDetails(true);
      setIsVisible(true);
    };
    return () => { openBannerFn = null; };
  }, []);

  const handleAcceptAll = () => {
    savePreferences({ necessary: true, analytics: true, marketing: true });
    setIsVisible(false);
    setShowDetails(false);
  };

  const handleDeclineAll = () => {
    savePreferences({ necessary: true, analytics: false, marketing: false });
    setIsVisible(false);
    setShowDetails(false);
  };

  const handleSaveCustom = () => {
    savePreferences({ necessary: true, analytics: analyticsEnabled, marketing: marketingEnabled });
    setIsVisible(false);
    setShowDetails(false);
  };

  const handleClose = () => {
    // Closing without explicit choice = decline all, but remember the decision
    savePreferences({ necessary: true, analytics: false, marketing: false });
    setIsVisible(false);
    setShowDetails(false);
  };

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </div>
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-3 md:p-4"
        >
          <div className="container max-w-2xl mx-auto">
            <div className="bg-background border border-border rounded-xl shadow-2xl px-4 py-3">
              {!showDetails ? (
                /* Compact banner — single row, big Accept CTA */
                <div className="flex items-center gap-3">
                  <Cookie className="h-5 w-5 text-primary shrink-0 hidden sm:block" />
                  <p className="text-xs sm:text-sm text-muted-foreground flex-1 leading-snug">
                    We use cookies to improve your experience.{' '}
                    <LocalizedLink to="/cookies" className="text-primary hover:underline">
                      Learn more
                    </LocalizedLink>
                  </p>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => { setShowDetails(true); setAnalyticsEnabled(false); setMarketingEnabled(false); }}
                      className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors"
                    >
                      Customize
                    </button>
                    <Button size="sm" onClick={handleAcceptAll} className="font-semibold px-5">
                      Accept All
                    </Button>
                  </div>
                </div>
              ) : (
                /* Detailed category view */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground flex items-center gap-2">
                      <Cookie className="h-5 w-5 text-primary" />
                      Cookie Settings
                    </h3>
                    <button
                      onClick={handleClose}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      aria-label="Close"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Necessary – always on */}
                  <div className="flex items-center justify-between bg-secondary/50 border border-border rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">Essential Cookies</p>
                        <p className="text-xs text-muted-foreground">Required for core functionality (cart, account, security) and anonymous session diagnostics for support purposes</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-muted-foreground bg-secondary px-2 py-1 rounded">Always on</span>
                  </div>

                  {/* Analytics – toggleable */}
                  <div className="flex items-center justify-between bg-secondary/50 border border-border rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">Analytics Cookies</p>
                        <p className="text-xs text-muted-foreground">Help us understand how visitors use our site (Google Analytics)</p>
                      </div>
                    </div>
                    <Toggle checked={analyticsEnabled} onChange={() => setAnalyticsEnabled(!analyticsEnabled)} />
                  </div>

                  {/* Marketing – toggleable */}
                  <div className="flex items-center justify-between bg-secondary/50 border border-border rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <Megaphone className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-foreground text-sm">Marketing Cookies</p>
                        <p className="text-xs text-muted-foreground">Used for visitor identification and business insights</p>
                      </div>
                    </div>
                    <Toggle checked={marketingEnabled} onChange={() => setMarketingEnabled(!marketingEnabled)} />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={handleDeclineAll} className="flex-1">
                      Decline All
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleSaveCustom} className="flex-1">
                      Save Preferences
                    </Button>
                    <Button size="sm" onClick={handleAcceptAll} className="flex-1">
                      Accept All
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};